import classNames from 'classnames';
import { ActiveTabGuard } from 'components/active-tab-guard';
import { ProductsFormGuard } from 'components/common-form/product-form-guard';
import { FormAnalyticsProvider } from 'components/form-analytic-provider';
import { QuoteWorkspaceProvider } from 'contexts/quote-workspace.context';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useState } from 'react';
import { EssentialHeader } from 'routes/essential-workspace/components/header';
import { EssentialMain } from 'routes/essential-workspace/components/main';

import './style.scss';
import { EssentialRightPanel } from 'routes/essential-workspace/components/right-panel';
import { EssentialSidePanel } from 'routes/essential-workspace/components/side-panel';
import { selectCustomProductLayoutSections } from 'routes/essential-workspace/state/custom-layout.selectors';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';
import { essentialWorkspaceShowTabsPanelChanged } from 'state/workspaces/workspaces/workspace.actions';

function useToggleSidePanel() {
  const isOpened = useAppSelector(state => state.workspaces.workspaces.essential.isTabsPanelShown);
  const dispatch = useThunkDispatch();
  const toggleOpen = () => {
    dispatch(essentialWorkspaceShowTabsPanelChanged());
  };
  return [isOpened, toggleOpen] as const;
}

const EssentialPage = () => {
  const [isOpened, toggleOpen] = useToggleSidePanel();
  const [isRightOpened, toggleRightOpen] = useState(false);
  const sections = useAppSelector(selectCustomProductLayoutSections);
  const [currentSectionId, setCurrentSectionId] = useState<string>('');

  const activeTabId = useAssertedActiveTabId();
  const activeTabContent = useAppSelector(state => state.workspaces.essential[activeTabId]);
  const isProductFormScreen = activeTabContent?.isProductValidated === true;
  const isLayoutEditing = isRightOpened;

  const effectiveSectionId =
    currentSectionId && sections.some(section => section.sectionId === currentSectionId)
      ? currentSectionId
      : (sections[0]?.sectionId ?? '');

  const handleToggleRightPanel = () => {
    toggleRightOpen(previous => !previous);
  };

  return (
    <ActiveTabGuard workspace="essential">
      <FormAnalyticsProvider
        name="essential form"
        className={classNames('scrollbar-sm flex-grow-1 essential-ws', {
          'essential-ws-with-sidebar': isOpened,
          'essential-ws-with-right-sidebar': isRightOpened,
          'bg-lvl2': isProductFormScreen,
        })}
      >
        <EssentialHeader
          toggleSidePanel={toggleOpen}
          isSidePanelOpened={isOpened}
          isRightPanelOpened={isRightOpened}
          productValidated={isProductFormScreen}
          toggleRightPanel={handleToggleRightPanel}
        />
        <EssentialSidePanel toggleSidePanel={toggleOpen} isSidePanelOpened={isOpened} />
        <EssentialMain currentSectionId={effectiveSectionId} isLayoutEditing={isLayoutEditing} />
        <EssentialRightPanel
          toggleSidePanel={handleToggleRightPanel}
          isOpen={isRightOpened}
          currentSectionId={effectiveSectionId}
          setCurrentSection={setCurrentSectionId}
        />
      </FormAnalyticsProvider>
    </ActiveTabGuard>
  );
};

export default function EssentialWorkspace() {
  return (
    <WorkspaceAnalyticCategoryContext.Provider value="Primary market essential">
      <QuoteWorkspaceProvider value="essential">
        <ProductsFormGuard>
          <EssentialPage />
        </ProductsFormGuard>
      </QuoteWorkspaceProvider>
    </WorkspaceAnalyticCategoryContext.Provider>
  );
}
